import { useState, useEffect } from 'react'
import { Calendar, AlertCircle, ChevronDown } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { StandardDialog } from '@/components/common/dialogs/StandardDialog'
import { SpinnerIcon } from '@/components/ui/SpinnerIcon'
import { DatePicker } from '@/components/ui/date-picker'
import { PeriodService } from '@/services/periodService'
import { usePeriodStore } from '@/stores/periodStore'
import { getPeriodOptions } from '@/utils/periodHelpers'
import { toast } from 'sonner'

interface CreatePeriodDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: (periodId: string) => void
}

export function CreatePeriodDialog({
  open,
  onOpenChange,
  onSuccess,
}: CreatePeriodDialogProps) {
  const { currentUser } = useAuth()
  const { loadPeriods, periods } = usePeriodStore()

  // Form state
  const [selectedMonth, setSelectedMonth] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showAdvanced, setShowAdvanced] = useState(false)

  // Advanced options state
  const [useCustomDates, setUseCustomDates] = useState(false)
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>()
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>()
  const [payoutDate, setPayoutDate] = useState<Date | undefined>()
  const [customName, setCustomName] = useState('')

  // Get available months for selection
  const periodOptions = getPeriodOptions(12, 1)

  // Filter out periods that already exist
  const existingPeriodIds = new Set(periods.map((p) => p.id))
  const availableOptions = periodOptions.filter(
    (opt) => !existingPeriodIds.has(opt.value)
  )

  // Default to previous month (payroll lag pattern)
  useEffect(() => {
    if (open && availableOptions.length > 0) {
      // Find previous month (index 1 in options, since 0 is future month)
      const previousMonth = availableOptions.find((opt, idx) => idx === 2) // Skip future and current
      const defaultMonth = previousMonth?.value || availableOptions[0]?.value || ''
      setSelectedMonth(defaultMonth)
      setError(null)
      setShowAdvanced(false)
    }
  }, [open, availableOptions.length])

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedMonth('')
      setError(null)
      setLoading(false)
      setShowAdvanced(false)
      setUseCustomDates(false)
      setCustomStartDate(undefined)
      setCustomEndDate(undefined)
      setPayoutDate(undefined)
      setCustomName('')
    }
  }, [open])

  // Handle form submission
  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    setError(null)

    if (!currentUser) {
      setError('Du måste vara inloggad')
      return
    }

    // Validation
    if (useCustomDates) {
      if (!customStartDate) {
        setError('Välj ett startdatum')
        return
      }
      if (!customEndDate) {
        setError('Välj ett slutdatum')
        return
      }
      if (customEndDate <= customStartDate) {
        setError('Slutdatum måste vara efter startdatum')
        return
      }
    } else {
      if (!selectedMonth) {
        setError('Välj en månad')
        return
      }
    }

    try {
      setLoading(true)

      const period = await PeriodService.createPeriod(currentUser, {
        // Use custom dates if enabled, otherwise use month
        ...(useCustomDates
          ? { startDate: customStartDate, endDate: customEndDate }
          : { month: selectedMonth }),
        // Optional fields
        ...(payoutDate && { payoutDate }),
        ...(customName.trim() && { name: customName.trim() }),
      })

      // Reload periods in store
      await loadPeriods(currentUser)

      toast.success('Period skapad')
      onSuccess(period.id)
    } catch (err) {
      console.error('Error creating period:', err)
      const errorMessage =
        err instanceof Error ? err.message : 'Det gick inte att skapa perioden'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const dialogContent = (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Month selector */}
      <div className="space-y-2">
        <Label htmlFor="month-select" className="text-sm font-medium">
          Välj månad {!useCustomDates && '*'}
        </Label>
        <Select
          value={selectedMonth}
          onValueChange={setSelectedMonth}
          disabled={loading || useCustomDates}
        >
          <SelectTrigger id="month-select">
            <SelectValue placeholder="Välj månad..." />
          </SelectTrigger>
          <SelectContent>
            {availableOptions.length === 0 ? (
              <div className="p-2 text-sm text-muted-foreground">
                Alla tillgängliga perioder har redan skapats
              </div>
            ) : (
              availableOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Välj den månad du vill skapa en löneperiod för
        </p>
      </div>

      {/* Payout date (always visible) */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Utbetalningsdatum</Label>
        <DatePicker
          value={payoutDate}
          onChange={setPayoutDate}
          placeholder="Välj utbetalningsdatum"
          disabled={loading}
        />
        <p className="text-xs text-muted-foreground">
          När lönerna betalas ut (används för skattedeklaration)
        </p>
      </div>

      {/* Advanced options (collapsed by default) */}
      <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="flex items-center gap-2 text-muted-foreground"
          >
            <ChevronDown
              className={`h-4 w-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
            />
            Avancerade alternativ
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-4 space-y-4">
          {/* Custom date range toggle */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="use-custom-dates"
              checked={useCustomDates}
              onChange={(e) => setUseCustomDates(e.target.checked)}
              disabled={loading}
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor="use-custom-dates" className="text-sm font-normal cursor-pointer">
              Använd anpassat datumintervall
            </Label>
          </div>

          {/* Custom date pickers (shown when enabled) */}
          {useCustomDates && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Startdatum *</Label>
                <DatePicker
                  value={customStartDate}
                  onChange={setCustomStartDate}
                  placeholder="Välj startdatum"
                  disabled={loading}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Slutdatum *</Label>
                <DatePicker
                  value={customEndDate}
                  onChange={setCustomEndDate}
                  placeholder="Välj slutdatum"
                  disabled={loading}
                />
              </div>
            </div>
          )}

          {/* Custom name */}
          <div className="space-y-2">
            <Label htmlFor="custom-name" className="text-sm font-medium">
              Anpassat namn
            </Label>
            <Input
              id="custom-name"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder="t.ex. November 2025"
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground">
              Lämna tomt för att generera namn automatiskt
            </p>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Error display */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </form>
  )

  const dialogFooter = (
    <div className="flex gap-3">
      <Button
        variant="outline"
        onClick={() => onOpenChange(false)}
        disabled={loading}
        className="flex-1"
      >
        Avbryt
      </Button>
      <Button
        onClick={() => handleSubmit()}
        disabled={
          loading ||
          (useCustomDates
            ? !customStartDate || !customEndDate
            : !selectedMonth || availableOptions.length === 0)
        }
        className="flex-1"
      >
        {loading ? (
          <div className="flex items-center gap-2">
            <SpinnerIcon size="sm" />
            Skapar...
          </div>
        ) : (
          <>
            <Calendar className="w-4 h-4 mr-2" />
            Skapa period
          </>
        )}
      </Button>
    </div>
  )

  return (
    <StandardDialog
      open={open}
      onOpenChange={onOpenChange}
      title={
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Calendar className="w-5 h-5 text-blue-600" />
          </div>
          <span>Skapa ny löneperiod</span>
        </div>
      }
      description="Skapa en ny löneperiod för att börja hantera löner för en specifik månad."
      footer={dialogFooter}
      size="sm"
      scrollable={false}
    >
      {dialogContent}
    </StandardDialog>
  )
}
